
import { defineComponent, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { IMedia } from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '../../store/driveStore';
import {
  MEDIA_TYPES, NAVIGATION_PAGES, PHOTO_VIEW_LAYOUT, PHOTO_VIEW_TYPE, SHARE_SETTINGS,
} from '@/types/enums';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import AppSmallLoader from '@/components/UI/AppSmallLoader.vue';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import AppVideoPlayer from '@/components/AppVideoPlayer.vue';
import MixedMediaPopUp from '@/components/BottomPopUp/PopUpTypes/MixedMediaPopUp.vue';
import AppRenameFile from '@/components/AppRenameFile.vue';

export default defineComponent({
  emits: ['on-select'],
  props: {
    file: { type: Object as () => IMedia, required: true },
    hideTitle: { type: Boolean, default: false },
    hideDelete: { type: Boolean, default: false },
    columnView: { type: Boolean, default: false },
    preventOpen: { type: Boolean, default: false },
    hidePlay: { type: Boolean, default: false },
    thumbnail: { type: Boolean, default: false },
    selector: { type: Boolean, default: false },
    isParentSelect: { type: Boolean, default: false },
    selectedId: { type: String, default: '' },
    isMixed: { type: Boolean, default: false },
  },
  components: {
    AppQuestionPopUp,
    AppVideoPlayer,
    AppSmallLoader,
    AppLazyImage,
    MixedMediaPopUp,
    AppRenameFile,
  },
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const route = useRoute();

    const isReady = ref(false);

    const isQuestionPopUp = ref<boolean>(false);
    const fileRenamePopUp = ref(false);

    const isNetwork = computed(() => route.meta.isNetwork);
    const currentPage = computed(() => route.meta.navigationPage);
    const reshareAllowed = computed(() => props.file.shareSettings?.includes(SHARE_SETTINGS.RESHARE_ALLOWED));
    const isMediaSettingsPopUp = ref(false);

    function deleteFile() {
      isQuestionPopUp.value = false;
      driveStore.deleteMedia(props.file.id, MEDIA_TYPES.FILES);
    }

    function openFile() {
      if (props.preventOpen || props.selector) {
        return;
      }
      // driveStore.fullscreenFile = { ...props.file, isMixed: props.isMixed };
      driveStore.appPhotoViewMedia = { ...props.file, isMixed: props.isMixed };
    }

    const photoUrl = computed(() => {
      if (props.file?.owner?.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.file.owner.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });
    const authorName = computed(() => `${props.file.owner.firstName} ${props.file.owner.lastName}`);

    const showTitle = computed(() => {
      if (props.hideTitle) return false;
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_FILES
       || currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER
       || currentPage.value === NAVIGATION_PAGES.DRIVE
       || currentPage.value === NAVIGATION_PAGES.NETWORK
       || currentPage.value === NAVIGATION_PAGES.NETWORK_FILES) {
        return true;
      }
      return !(driveStore.photoViewLayout === PHOTO_VIEW_LAYOUT.GRID);
    });

    const videUrl = computed(() => process.env.VUE_APP_MEDIA_URL + props.file.fileUrl);
    const thumbUrl = computed(() => process.env.VUE_APP_MEDIA_URL + props.file.previewUrl);

    function selectVideo(file: IMedia): void {
      if (!props.selector) {
        return;
      }
      if (props.isParentSelect) {
        emit('on-select', props.file.id);
      } else {
        // eslint-disable-next-line no-param-reassign
        file.selected = !file.selected;
      }
    }

    const isSelectedFromParent = computed(() => props.file.id === props.selectedId);

    return {
      mainStore,
      isQuestionPopUp,
      isNetwork,
      photoUrl,
      videUrl,
      authorName,
      isReady,
      thumbUrl,
      isSelectedFromParent,
      deleteFile,
      openFile,
      selectVideo,
      driveStore,
      showTitle,
      reshareAllowed,
      PHOTO_VIEW_LAYOUT,
      isMediaSettingsPopUp,
      fileRenamePopUp,
    };
  },
});
